'use client'

import { useEffect, useId, useRef, useState, type KeyboardEvent, type RefObject } from 'react'
import { ArrowUp, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { UserText } from '@/components/ui/user-text'
import { useTranslation } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { KNOWLEDGE_QUESTION_MAX, type DocumentCitation, type KnowledgeEscalation } from '@/lib/knowledge/portal-types'
import { AnswerView } from './answer-view'
import type { ThreadEntry } from './use-answer-session'

/**
 * The conversation and the question box, shared by the portal Ask panel and the
 * admin "Try it" panel.
 *
 * The thread is a polite live region (role="log"), so a screen reader hears the
 * answer as it arrives without being interrupted mid-sentence.
 */
export function AskThread({
  entries,
  label,
  escalation,
  onOpenSource,
  libraryHref,
}: {
  entries: ThreadEntry[]
  label: string
  escalation: KnowledgeEscalation | null
  onOpenSource: (citation: DocumentCitation) => void
  libraryHref: string | null
}) {
  const { t } = useTranslation()
  const endRef = useRef<HTMLDivElement>(null)
  const last = entries[entries.length - 1]
  const lastText = last?.kind === 'answer' ? last.turn.text : ''
  // Follow the answer inside the panel's own scroller ([data-ask-scroll]) only:
  // scrollIntoView would also scroll the page around it (the admin settings
  // page, for Try it) on every streamed word.
  useEffect(() => {
    const scroller = endRef.current?.closest<HTMLElement>('[data-ask-scroll]')
    if (scroller) scroller.scrollTop = scroller.scrollHeight
  }, [entries.length, lastText])

  return (
    <div role="log" aria-live="polite" aria-relevant="additions text" aria-label={label} className="space-y-4">
      {entries.map((entry) =>
        entry.kind === 'question' ? (
          <div key={entry.id} className="ms-auto w-fit max-w-[85%] rounded-2xl rounded-ee-sm bg-primary px-3 py-2 text-sm text-primary-foreground">
            <span className="sr-only">{t('knowledgePortal.panel.you')}: </span>
            <UserText className="whitespace-pre-wrap">{entry.text}</UserText>
          </div>
        ) : (
          <div key={entry.id} className="max-w-full">
            <AnswerView turn={entry.turn} escalation={escalation} onOpenSource={onOpenSource} libraryHref={libraryHref} />
          </div>
        ),
      )}
      <div ref={endRef} />
    </div>
  )
}

export function AskComposer({
  onAsk,
  locked,
  busy,
  inputLabel,
  placeholder,
  inputRef,
  variant = 'panel',
}: {
  onAsk: (question: string) => void
  /** A refusal asking again cannot fix: the box itself is switched off. */
  locked: boolean
  /** An answer is streaming: typing is fine, sending waits. */
  busy: boolean
  inputLabel: string
  placeholder: string
  inputRef?: RefObject<HTMLTextAreaElement | null>
  /**
   * `panel`: a box and a round send button side by side (the admin "Try it"
   * panel). `drawer`: the main application's composer (`ai-chat/chat-input.tsx`):
   * one rounded shell with the send button inside its top inline-end corner,
   * and the Enter / Shift+Enter hint under it.
   */
  variant?: 'panel' | 'drawer'
}) {
  const { t } = useTranslation()
  const hintId = useId()
  const [value, setValue] = useState('')
  const question = value.trim()
  const tooLong = question.length > KNOWLEDGE_QUESTION_MAX
  const canSend = !locked && !busy && question.length > 0 && !tooLong

  const send = () => {
    if (!canSend) return
    onAsk(question)
    setValue('')
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault()
      send()
    }
  }

  const tooLongHint = tooLong ? (
    <p id={hintId} role="alert" className="mt-1 text-xs text-destructive">
      {t('knowledgePortal.errors.tooLong.body')}
    </p>
  ) : null

  if (variant === 'drawer') {
    return (
      <form
        onSubmit={(event) => {
          event.preventDefault()
          send()
        }}
      >
        <div
          className={cn(
            'relative w-full rounded-xl border border-border bg-muted/30 transition-colors',
            'focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/40',
            locked && 'opacity-50',
          )}
        >
          <Textarea
            ref={inputRef}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={onKeyDown}
            aria-label={inputLabel}
            aria-invalid={tooLong || undefined}
            aria-describedby={tooLong ? hintId : undefined}
            placeholder={placeholder}
            disabled={locked}
            rows={3}
            className="max-h-[20rem] min-h-[6.5rem] resize-none rounded-xl border-0 bg-transparent px-4 py-3 pe-14 text-sm shadow-none focus-visible:ring-0 dark:bg-transparent"
          />
          <Button
            type="submit"
            size="icon"
            aria-label={t('knowledgePortal.panel.send')}
            title={t('knowledgePortal.panel.send')}
            disabled={!canSend}
            className="absolute end-2 top-2 size-9 rounded-lg"
          >
            <Send className="rtl-flip size-4" aria-hidden="true" />
          </Button>
        </div>
        {tooLongHint}
      </form>
    )
  }

  return (
    <form
      className="flex items-end gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        send()
      }}
    >
      <div className="min-w-0 flex-1">
        <Textarea
          ref={inputRef}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={onKeyDown}
          aria-label={inputLabel}
          aria-invalid={tooLong || undefined}
          aria-describedby={tooLong ? hintId : undefined}
          placeholder={placeholder}
          disabled={locked}
          rows={2}
          className="max-h-40 min-h-11 resize-none"
        />
        {tooLongHint}
      </div>
      <Button type="submit" size="icon" aria-label={t('knowledgePortal.panel.send')} disabled={!canSend} className="shrink-0 rounded-full">
        <ArrowUp aria-hidden="true" />
      </Button>
    </form>
  )
}
